/**
 * The Usuarios line under a user's status, and the dialog behind it (#114).
 *
 * Until #114 the office was told a letter "will be sent" and nothing else, whatever happened to it.
 * The line says what did happen; the dialog says why, in plain words, and quotes the receiving
 * server, because the refusals worth knowing about are the ones nobody anticipated.
 */

import { useTranslation } from 'react-i18next'
import type { EmailDelivery, User } from '@deca/api'

import { deliveryTone, explanationKey, isAddressProblem, isDeliveryFailure } from '../deca/emailDelivery.ts'
import { formatDateTime } from '../deca/format.ts'
import { Dialog } from './Dialog.tsx'
import { Icon, type IconName } from './Icon.tsx'
import { Button, Datum, Notice } from './primitives.tsx'

const ICON: Record<ReturnType<typeof deliveryTone>, IconName> = {
  success: 'checkCircle',
  info: 'clock',
  warning: 'alert',
  danger: 'alert',
}

function useHeadline(delivery: EmailDelivery): string {
  const { t } = useTranslation()
  return t('admin.users.mail.line', {
    kind: t(`admin.users.mail.kind.${delivery.kind}`),
    status: t(`admin.users.mail.status.${delivery.status}`),
  })
}

export function EmailDeliveryLine({ delivery, onOpen }: { delivery: EmailDelivery; onOpen: () => void }) {
  const { t, i18n } = useTranslation()
  const tone = deliveryTone(delivery)
  // While it is being handed to SES there is no moment worth printing yet.
  const when = delivery.status === 'SENDING' ? null : formatDateTime(delivery.statusAt, i18n.language)

  // Short, and in parts that each keep to the width of the chip above: the status column is the
  // narrowest in the table, and anything wider pushes the row's actions off the card. Which letter
  // it was, and the rest, is the dialog's to say.
  return (
    <button
      type="button"
      className={`link-button mail-line mail-line--${tone}`}
      title={t('admin.users.mail.seeDetails')}
      onClick={onOpen}
    >
      <Icon name={ICON[tone]} size={14} />
      <span className="mail-line__text">
        <span>{t(`admin.users.mail.short.${delivery.status}`)}</span>
        {when ? <span className="mail-line__when">{when}</span> : null}
        {isDeliveryFailure(delivery) ? <span className="mail-line__why">{t('admin.users.mail.seeWhy')}</span> : null}
      </span>
    </button>
  )
}

/**
 * A user's address in the Usuarios list: muted as it always was, and red, with the reason, when
 * its domain cannot receive mail (`User.emailUndeliverable`, #141).
 *
 * The address itself is the problem there, so it is said on the address, not in the delivery
 * line: that line needs a letter, and no letter goes to such an address any more. The two found on
 * production on 2026-09-29 were created before the domain check existed, and their invitations
 * never arrived.
 */
export function UserAddress({ user, canChangeEmail }: { user: User; canChangeEmail: boolean }) {
  const { t } = useTranslation()
  const dead = user.emailUndeliverable ?? null
  if (!dead) return <span className="small muted">{user.email}</span>

  return (
    <span className="user-address user-address--undeliverable small">
      <span className="user-address__email">
        <Icon name="alert" size={14} />
        {user.email}
      </span>
      <span className="user-address__why">
        {t(`admin.users.address.${dead.reason}`, { domain: dead.domain })}{' '}
        {canChangeEmail
          ? t('admin.users.address.fix', { action: t('admin.users.changeEmail') })
          : t('admin.users.address.ask', { operator: t('app.operator') })}
      </span>
    </span>
  )
}

export function EmailDeliveryDialog({
  delivery,
  onClose,
  onChangeEmail,
}: {
  delivery: EmailDelivery | null
  onClose: () => void
  /**
   * Present for a platform administrator, the one role that may change a sign-in address (#71).
   * Offered only when the address is the problem; anyone else is told whom to ask.
   */
  onChangeEmail?: (() => void) | undefined
}) {
  const { t } = useTranslation()

  return (
    <Dialog
      open={delivery !== null}
      title={t('admin.users.mail.dialogTitle')}
      subtitle={delivery?.recipient}
      onClose={onClose}
      footer={
        <>
          {delivery && isAddressProblem(delivery) && onChangeEmail ? (
            <Button onClick={onChangeEmail}>{t('admin.users.changeEmail')}</Button>
          ) : null}
          <Button variant="primary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </>
      }
    >
      {delivery ? <DeliveryBody delivery={delivery} canChangeEmail={onChangeEmail !== undefined} /> : null}
    </Dialog>
  )
}

function DeliveryBody({ delivery, canChangeEmail }: { delivery: EmailDelivery; canChangeEmail: boolean }) {
  const { t, i18n } = useTranslation()
  const headline = useHeadline(delivery)

  return (
    <div className="stack">
      <Notice tone={deliveryTone(delivery)} title={headline}>
        {t(explanationKey(delivery))}
        {isAddressProblem(delivery) && !canChangeEmail
          ? ` ${t('admin.users.mail.askOperator', { operator: t('app.operator') })}`
          : null}
      </Notice>

      <div className="datum-grid">
        <Datum label={t('admin.users.mail.sentAt')} value={formatDateTime(delivery.sentAt, i18n.language)} tabular />
        <Datum label={t('admin.users.mail.statusAt')} value={formatDateTime(delivery.statusAt, i18n.language)} tabular />
      </div>

      {delivery.detail ? (
        <Datum
          label={t('admin.users.mail.serverReply')}
          // Verbatim: the one line an administrator can forward to the recipient's IT.
          value={<code className="mono small mail-reply">{delivery.detail}</code>}
        />
      ) : null}
    </div>
  )
}
